/**
 * A date field with the console's own calendar popover.
 *
 * Exists because the native `<input type="date">` popup cannot be styled at
 * all — it rendered as a stock OS calendar floating over a themed panel. The
 * value contract is kept identical to the native input (`YYYY-MM-DD`, "" for
 * unset), so the callers' UTC-midnight date logic is untouched.
 *
 * Dates here are plain {year, month, day} numbers. `Date` is used only as a
 * day-arithmetic tool with local components, never via `toISOString`, so no
 * timezone can shift a picked day.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { CalendarBlankIcon, CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import { Button } from "./ui.tsx";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Monday-first, matching how a Swedish partner reads a week. */
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

interface Ymd {
  y: number;
  /** 0-based, like `Date`. */
  m: number;
  d: number;
}

function parseYmd(value: string): Ymd | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]) - 1, d: Number(match[3]) };
}

function toKey(date: Ymd): string {
  const mm = String(date.m + 1).padStart(2, "0");
  const dd = String(date.d).padStart(2, "0");
  return `${date.y}-${mm}-${dd}`;
}

function localToday(): Ymd {
  const now = new Date();
  return { y: now.getFullYear(), m: now.getMonth(), d: now.getDate() };
}

function shiftDays(date: Ymd, delta: number): Ymd {
  const shifted = new Date(date.y, date.m, date.d + delta);
  return { y: shifted.getFullYear(), m: shifted.getMonth(), d: shifted.getDate() };
}

function daysInMonth(y: number, m: number): number {
  return new Date(y, m + 1, 0).getDate();
}

/** Always 42 cells (6 weeks), padded with the neighbour months, so the
 * popover never changes height while paging. */
function gridDays(y: number, m: number): Ymd[] {
  const lead = (new Date(y, m, 1).getDay() + 6) % 7;
  const first = shiftDays({ y, m, d: 1 }, -lead);
  return Array.from({ length: 42 }, (_, index) => shiftDays(first, index));
}

export function DateField({
  label,
  value,
  placeholder,
  disabled,
  onPick,
}: {
  label: string;
  /** `YYYY-MM-DD`, or "" for unset — the native date-input contract. */
  value: string;
  /** What an unset bound means, e.g. "Open-ended". */
  placeholder: string;
  disabled?: boolean;
  /** Receives the picked day as `YYYY-MM-DD`, or "" when cleared. */
  onPick: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => {
    const base = parseYmd(value) ?? localToday();
    return { y: base.y, m: base.m };
  });
  const [focusDay, setFocusDay] = useState<Ymd>(() => parseYmd(value) ?? localToday());
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const labelId = useId();

  const selected = parseYmd(value);
  const today = localToday();

  const openCalendar = () => {
    const base = selected ?? today;
    setView({ y: base.y, m: base.m });
    setFocusDay(base);
    setOpen(true);
  };

  const close = (refocusTrigger: boolean) => {
    setOpen(false);
    if (refocusTrigger) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close(true);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Roving focus: the focused day is the one tabbable cell, and moving it
  // (arrows, paging) moves real DOM focus with it.
  useLayoutEffect(() => {
    if (!open) return;
    gridRef.current
      ?.querySelector<HTMLButtonElement>(`[data-key="${toKey(focusDay)}"]`)
      ?.focus();
  }, [open, focusDay]);

  const moveFocus = (deltaDays: number) => {
    const next = shiftDays(focusDay, deltaDays);
    setFocusDay(next);
    setView({ y: next.y, m: next.m });
  };

  const moveFocusMonths = (deltaMonths: number) => {
    const base = new Date(focusDay.y, focusDay.m + deltaMonths, 1);
    const next = {
      y: base.getFullYear(),
      m: base.getMonth(),
      d: Math.min(focusDay.d, daysInMonth(base.getFullYear(), base.getMonth())),
    };
    setFocusDay(next);
    setView({ y: next.y, m: next.m });
  };

  const moveMonth = (delta: number) => {
    const shifted = new Date(view.y, view.m + delta, 1);
    setView({ y: shifted.getFullYear(), m: shifted.getMonth() });
  };

  const pick = (day: Ymd) => {
    onPick(toKey(day));
    close(true);
  };

  const onGridKeyDown = (event: React.KeyboardEvent) => {
    const handlers: Record<string, () => void> = {
      ArrowLeft: () => moveFocus(-1),
      ArrowRight: () => moveFocus(1),
      ArrowUp: () => moveFocus(-7),
      ArrowDown: () => moveFocus(7),
      Home: () => setFocusDay({ y: view.y, m: view.m, d: 1 }),
      End: () => setFocusDay({ y: view.y, m: view.m, d: daysInMonth(view.y, view.m) }),
      PageUp: () => moveFocusMonths(-1),
      PageDown: () => moveFocusMonths(1),
    };
    const handler = handlers[event.key];
    if (handler) {
      event.preventDefault();
      handler();
    }
  };

  const days = gridDays(view.y, view.m);
  const selectedKey = selected ? toKey(selected) : null;
  const todayKey = toKey(today);
  // After paging with the header carets the focused day can leave the visible
  // grid; the first of the shown month becomes the tabbable cell instead.
  const focusKey = days.some((day) => toKey(day) === toKey(focusDay))
    ? toKey(focusDay)
    : toKey({ y: view.y, m: view.m, d: 1 });

  return (
    <div className="datefield" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="datefield__trigger"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? close(false) : openCalendar())}
      >
        <span className="datefield__label" id={labelId}>
          {label}
        </span>
        <span className="datefield__value" data-empty={selected ? undefined : true}>
          <CalendarBlankIcon size={14} className="datefield__icon" aria-hidden="true" />
          {selected
            ? `${selected.d} ${(MONTHS[selected.m] ?? "").slice(0, 3)} ${selected.y}`
            : placeholder}
        </span>
      </button>

      {open && (
        <div className="cal" role="dialog" aria-labelledby={labelId}>
          <div className="cal__head">
            <span className="cal__month">
              {MONTHS[view.m]} {view.y}
            </span>
            <span className="cal__nav">
              <button
                type="button"
                className="icon-btn"
                aria-label="Previous month"
                onClick={() => moveMonth(-1)}
              >
                <CaretLeftIcon size={14} weight="bold" />
              </button>
              <button
                type="button"
                className="icon-btn"
                aria-label="Next month"
                onClick={() => moveMonth(1)}
              >
                <CaretRightIcon size={14} weight="bold" />
              </button>
            </span>
          </div>

          <div className="cal__week" aria-hidden="true">
            {WEEKDAYS.map((day) => (
              <span className="cal__wd" key={day}>
                {day}
              </span>
            ))}
          </div>

          <div className="cal__grid" ref={gridRef} onKeyDown={onGridKeyDown}>
            {days.map((day) => {
              const key = toKey(day);
              return (
                <button
                  key={key}
                  type="button"
                  className="cal__day"
                  data-key={key}
                  data-outside={day.m !== view.m || undefined}
                  data-today={key === todayKey || undefined}
                  aria-pressed={key === selectedKey}
                  aria-label={`${day.d} ${MONTHS[day.m]} ${day.y}`}
                  tabIndex={key === focusKey ? 0 : -1}
                  onClick={() => pick(day)}
                >
                  {day.d}
                </button>
              );
            })}
          </div>

          <div className="cal__foot">
            <Button variant="quiet" size="sm" onClick={() => pick(today)}>
              Today
            </Button>
            {selected && (
              <Button
                variant="quiet"
                size="sm"
                onClick={() => {
                  onPick("");
                  close(true);
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
